import React, { useState, useEffect, useReducer, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthorizationListComponent from './AuthorizationListComponent';
import { reducer, initialState } from '../../reducers/authorization/containerReducer';
import { SAVE_AUTHORIZATIONS_X_USER_GROUPS, UNREFINE_AUTHORIZATION } from '../../mutations';
import { UNREFINE_AUTHORIZATION_INVALIDATED, AUTHORIZATIONS_X_USER_GROUPS_INVALIDATED } from '../../subscriptions';
import { useApolloClient } from '@apollo/client';
import { useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import Fragments from '../../fragments';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { authorizationStore } from './AuthorizationListBrowser';
import { RightsContext } from 'providers/RightsContext';
import { Box } from '@mui/material';

const AuthorizationListContainer = ({ onSaveFnChanged, onRefineAuthorization, stateRef }) => {
  const { refetchRightsInfo } = useContext(RightsContext);
  const { store } = useListBrowserUtils(authorizationStore);
  const client = useApolloClient();
  const addToast = useToast();
  const [t] = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [rendered, setRendered] = useState(false);

  const [saveAuthorizationsXUserGroups] = useMutation(SAVE_AUTHORIZATIONS_X_USER_GROUPS);

  const [unrefineAuthorization] = useMutation(UNREFINE_AUTHORIZATION);

  useSubscription(UNREFINE_AUTHORIZATION_INVALIDATED, {
    onData: unrefineAuthorizationData => {
      const { authorizationId } = unrefineAuthorizationData.data.data.unrefineAuthorizationInvalidated;

      client.cache.modify({
        id: `Authorization:{"webAuthorizationId":${authorizationId}}`,
        fields: {
          isRefined() {
            return false;
          }
        }
      });
      client.cache.modify({
        id: `AuthorizationWithRefines:{"webAuthorizationId":${authorizationId}}`,
        fields: {
          authorizationXDocumentTypeXUserGroups() {
            return [];
          }
        }
      });
      client.cache.gc();
      dispatch({ type: 'OnUnrefineAuthorization', authorizationId });
      addToast(t('Authorization.UnrefineAuthorizationSaved'), 'success');
    }
  });

  useSubscription(AUTHORIZATIONS_X_USER_GROUPS_INVALIDATED, {
    onData: authorizationsXUserGroupsInvalidatedData => {
      const { authorizationsXUserGroups } = authorizationsXUserGroupsInvalidatedData.data.data.authorizationsXUserGroupsInvalidated;
      authorizationsXUserGroups.forEach(axug => {
        if (axug.canRead) {
          const newAuthorizationXUserGroup = {
            __typename: 'AuthorizationXUserGroup',
            authorizationId: axug.authorizationId,
            userGroupId: axug.userGroupId,
            canRead: axug.canRead
          };
          client.cache.modify({
            id: `Authorization:{"webAuthorizationId":${axug.authorizationId}}`,
            fields: {
              authorizationXUserGroups(existingAuthorizationXUserGroups = [], { readField }) {
                const newRef = client.cache.writeFragment({
                  data: newAuthorizationXUserGroup,
                  fragment: Fragments.authorizationXUserGroup
                });

                if (
                  existingAuthorizationXUserGroups.some(
                    ref =>
                      newAuthorizationXUserGroup.authorizationId === readField('authorizationId', ref) &&
                      newAuthorizationXUserGroup.userGroupId === readField('userGroupId', ref)
                  )
                ) {
                  return existingAuthorizationXUserGroups;
                }

                return [...existingAuthorizationXUserGroups, newRef];
              }
            }
          });
        } else {
          client.cache.modify({
            id: `Authorization:{"webAuthorizationId":${axug.authorizationId}}`,
            fields: {
              authorizationXUserGroups(existingAuthorizationXUserGroups, { readField }) {
                return existingAuthorizationXUserGroups.filter(ref => axug.userGroupId !== readField('userGroupId', ref));
              }
            }
          });
        }
      });
      client.cache.gc();
      refetchRightsInfo();
      dispatch({ type: 'OnResetChangedPermissions' });
      addToast(t('Authorization.AuthorizationsXUserGroupsSaved'), 'success');
    }
  });

  const convertToObject = authXUG => {
    let object = [];
    for (var auth in authXUG) {
      for (var ug in authXUG[auth]) {
        object.push({ authorizationId: parseInt(auth), userGroupId: parseInt(ug), canRead: authXUG[auth][ug].value });
      }
    }
    return object;
  };

  const onSave = useCallback(() => {
    if (!localState.disabledSave) {
      saveAuthorizationsXUserGroups({ variables: { input: convertToObject(localState.changedPermissions) } });
      dispatch({ type: 'OnSaveAuthorizationsXUserGroups' });
    }
  }, [localState.changedPermissions, localState.disabledSave, saveAuthorizationsXUserGroups]);

  const onUnrefineAuthorization = index => {
    const { webAuthorizationId: authorizationId } = localState.authorizations[index];
    if (authorizationId) {
      unrefineAuthorization({ variables: { input: { authorizationId: authorizationId } } });
    }
  };

  const handleUserGroupsChange = userGroups => dispatch({ type: 'OnInitUserGroups', userGroups });

  useEffect(() => {
    if (stateRef && stateRef.current) {
      stateRef.current = {
        ...stateRef.current,
        handleUserGroupsChange
      };
      dispatch({
        type: 'OnInitUserGroups',
        userGroups:
          stateRef.current.data?.filter(x => x.name.toLowerCase().includes(store.filters.userGroups?.toLowerCase() ?? '') ?? true) || []
      });
      dispatch({
        type: 'OnInitChangedPermissions',
        changedPermissions: stateRef.current.changedPermissions || {}
      });
      dispatch({
        type: 'OnInitDisabledSave',
        disabledSave: stateRef.current.disabledSave || stateRef.current.disabledSave === false ? stateRef.current.disabledSave : true
      });
    }
  }, [stateRef, store.filters.userGroups]);

  useEffect(() => {
    stateRef.current = {
      ...stateRef.current,
      changedPermissions: localState.changedPermissions
    };
  }, [localState.changedPermissions, stateRef]);

  useEffect(() => {
    stateRef.current = {
      ...stateRef.current,
      disabledSave: localState.disabledSave
    };
  }, [localState.disabledSave, stateRef]);

  useEffect(() => {
    if (store.values) dispatch({ type: 'OnInitAuthorizations', list: store.values });
  }, [store.values]);

  useEffect(() => {
    if (!rendered) return;
    onSaveFnChanged(onSave, localState.disabledSave);
  }, [localState.disabledSave, onSave, onSaveFnChanged, rendered]);

  useEffect(() => {
    setRendered(true);
  }, [rendered]);

  return (
    <Box mt={2}>
      <Prompt when={!localState.disabledSave} message={t('Authorization.LeavingWithoutSavingAuthorizations')} />
      <AuthorizationListComponent
        authorizations={localState.authorizations}
        userGroups={localState.userGroups}
        onRefineAuthorization={onRefineAuthorization}
        onUnrefineAuthorization={onUnrefineAuthorization}
        loading={store.loading}
        dispatch={dispatch}
        changedPermissions={localState.changedPermissions}
      />
    </Box>
  );
};

AuthorizationListContainer.propTypes = {
  onRefineAuthorization: PropTypes.func.isRequired,
  onSaveFnChanged: PropTypes.func.isRequired,
  stateRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
};

export default AuthorizationListContainer;
